import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  BugAntIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';
import FileUpload from '../components/FileUpload';
import PasteScreenshotTextarea from '../components/PasteScreenshotTextarea';
import Lottie from 'lottie-react';
import successCheckmark from '../assets/animations/success-checkmark.json';
import successStars from '../assets/animations/success-stars.json';
import successRocket from '../assets/animations/success-rocket.json';

const STORAGE_KEY = 'ticketSuccessAnimationIndex';

const animations = [
  { data: successCheckmark, label: 'Your issue is on its way!' },
  { data: successStars, label: "You're a star for reporting this!" },
  { data: successRocket, label: 'Issue launched successfully!' },
];

const severities = ['1 - Critical', '2 - High', '3 - Medium', '4 - Low'];
const priorities = [
  { value: '', label: 'Select…' },
  { value: 1, label: '1 — Critical / blocking' },
  { value: 2, label: '2 — High' },
  { value: 3, label: '3 — Medium' },
  { value: 4, label: '4 — Low' },
];
const reproducibilityOptions = ['Always', 'Sometimes', 'Rarely', 'Unable to reproduce'];
const environments = ['Production', 'UAT', 'Dev', 'Local'];

function formatObservedAt(dateValue) {
  if (!dateValue) return '';
  const [year, month, day] = dateValue.split('-');
  if (!year || !month || !day) return dateValue;
  return `${day}/${month}/${year}`;
}

function getNextAnimationIndex() {
  const stored = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
  const next = (stored + 1) % animations.length;
  localStorage.setItem(STORAGE_KEY, String(next));
  return stored;
}

function FieldLabel({ children, hint, required }) {
  return (
    <label className="block text-sm font-medium text-slate-800 mb-1.5">
      {children}
      {required && <span className="text-red-500"> *</span>}
      {hint && <span className="font-normal text-slate-400"> — {hint}</span>}
    </label>
  );
}

const inputClass =
  'w-full px-3 py-2.5 border border-slate-200 rounded-lg bg-white text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-400/40 focus:border-sky-400 placeholder:text-slate-400';

const inputErrorClass =
  'w-full px-3 py-2.5 border border-red-400 rounded-lg bg-white text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-400 placeholder:text-slate-400';

const sectionClass = 'rounded-2xl border border-sky-100 bg-sky-50/60 p-5 sm:p-6';

const ExergyIssueForm = () => {
  const navigate = useNavigate();
  const [workItemType, setWorkItemType] = useState('Bug');
  const [formData, setFormData] = useState({
    title: '',
    severity: '3 - Medium',
    priority: '',
    reproducibility: 'Always',
    featureWorkflow: '',
    environment: 'UAT',
    buildVersion: '',
    modulePage: '',
    urlScreenPath: '',
    browserDevice: '',
    observedAt: '',
    userAccount: '',
    recordReference: '',
    sampleDataNotes: '',
    stepsToReproduce: '',
    expectedResult: '',
    actualResult: '',
    additionalNotes: '',
    asARole: '',
    iWantGoal: '',
    soThatBenefit: '',
    storyDescription: '',
    acceptanceCriteria: '',
    businessValue: '',
    dependenciesNotes: '',
    mockupLink: '',
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [animationIndex, setAnimationIndex] = useState(null);
  const [created, setCreated] = useState(null);

  const isBug = workItemType === 'Bug';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'priority' && value !== '' ? parseInt(value, 10) : value,
    }));
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const fieldClass = (name) => (fieldErrors[name] ? inputErrorClass : inputClass);

  const handleScreenshotPaste = (file) => {
    if (selectedFiles.length >= 5) {
      setWarning('Maximum 5 attachments — remove one to paste another screenshot.');
      return;
    }
    setSelectedFiles((prev) => [...prev, file]);
    setWarning(null);
  };

  const validate = () => {
    const errors = {};
    const require = (name, value) => {
      if (!String(value ?? '').trim() && value !== 0) errors[name] = true;
    };

    require('title', formData.title);
    require('priority', formData.priority);
    require('modulePage', formData.modulePage);

    if (isBug) {
      require('severity', formData.severity);
      require('reproducibility', formData.reproducibility);
      require('environment', formData.environment);
      require('buildVersion', formData.buildVersion);
      require('browserDevice', formData.browserDevice);
      require('observedAt', formData.observedAt);
      require('stepsToReproduce', formData.stepsToReproduce);
      require('expectedResult', formData.expectedResult);
      require('actualResult', formData.actualResult);
    } else {
      require('asARole', formData.asARole);
      require('iWantGoal', formData.iWantGoal);
      require('soThatBenefit', formData.soThatBenefit);
      require('acceptanceCriteria', formData.acceptanceCriteria);
      require('businessValue', formData.businessValue);
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return 'Please complete the highlighted fields.';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!authService.isAuthenticated()) {
      setError('You must be logged in to log an issue');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setWarning(null);

      const payload = isBug
        ? {
            workItemType: 'Bug',
            title: formData.title.trim(),
            severity: formData.severity,
            priority: formData.priority,
            reproducibility: formData.reproducibility,
            featureWorkflow: formData.featureWorkflow.trim() || null,
            environment: formData.environment,
            buildVersion: formData.buildVersion.trim(),
            modulePage: formData.modulePage.trim(),
            urlScreenPath: formData.urlScreenPath.trim() || null,
            browserDevice: formData.browserDevice.trim(),
            observedAt: formatObservedAt(formData.observedAt),
            userAccount: formData.userAccount.trim() || null,
            recordReference: formData.recordReference.trim() || null,
            sampleDataNotes: formData.sampleDataNotes.trim() || null,
            stepsToReproduce: formData.stepsToReproduce.trim(),
            expectedResult: formData.expectedResult.trim(),
            actualResult: formData.actualResult.trim(),
            additionalNotes: formData.additionalNotes.trim() || null,
          }
        : {
            workItemType: 'UserStory',
            title: formData.title.trim(),
            priority: formData.priority,
            modulePage: formData.modulePage.trim(),
            asARole: formData.asARole.trim(),
            iWantGoal: formData.iWantGoal.trim(),
            soThatBenefit: formData.soThatBenefit.trim(),
            storyDescription: formData.storyDescription.trim() || null,
            acceptanceCriteria: formData.acceptanceCriteria.trim(),
            businessValue: formData.businessValue.trim(),
            dependenciesNotes: formData.dependenciesNotes.trim() || null,
            mockupLink: formData.mockupLink.trim() || null,
          };

      const result = await apiService.createExergyIssue(payload);
      const ticketId = result.ticket?.id;

      if (ticketId && selectedFiles.length > 0) {
        setUploadingFiles(true);
        try {
          for (const file of selectedFiles) {
            await apiService.uploadAttachment(ticketId, file);
          }
        } catch (uploadError) {
          console.error('Attachment upload failed:', uploadError);
          setWarning('Issue created, but one or more attachments failed to upload.');
        } finally {
          setUploadingFiles(false);
        }
      }

      if (result.devOpsError) {
        setWarning(
          `Helpdesk ticket #${ticketId} was created, but Azure DevOps failed: ${result.devOpsError}`
        );
      }

      setCreated(result);
      setAnimationIndex(getNextAnimationIndex());
      setSubmitted(true);

      if (result.devOpsError) {
        return;
      }

      setTimeout(() => {
        if (ticketId) navigate(`/ticket/${ticketId}`);
        else navigate('/dashboard');
      }, 3500);
    } catch (err) {
      console.error('Failed to create Exergy issue:', err);
      setError(err.message || 'Failed to create issue. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const currentUser = authService.getUserProfile();
  const userName = authService.getUserName() || 'Unknown User';
  const currentAnimation = useMemo(
    () => (animationIndex !== null ? animations[animationIndex] : null),
    [animationIndex]
  );

  if (submitted && currentAnimation) {
    return (
      <div className="max-w-2xl mx-auto flex flex-col items-center justify-center py-16">
        <div className="w-64 h-64 mb-6">
          <Lottie animationData={currentAnimation.data} loop={false} autoplay style={{ width: '100%', height: '100%' }} />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Issue logged</h2>
        <p className="text-slate-600 text-lg mb-2">{currentAnimation.label}</p>
        {created?.ticket?.id && (
          <p className="text-sm text-slate-500 mb-1">Helpdesk ticket #{created.ticket.id}</p>
        )}
        {created?.workItemId && (
          <a
            href={created.workItemUrl}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-sky-700 hover:underline mb-4"
          >
            Azure DevOps {created.workItemType} #{created.workItemId}
          </a>
        )}
        {warning && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm text-center max-w-md">
            {warning}
          </div>
        )}
        <button
          onClick={() => navigate(created?.ticket?.id ? `/ticket/${created.ticket.id}` : '/dashboard')}
          className="px-6 py-2 rounded-lg text-sm font-medium text-white bg-sky-600 hover:bg-sky-700"
        >
          Open ticket
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto pb-10">
      <button
        onClick={() => navigate('/dashboard')}
        className="flex items-center text-sm text-slate-600 hover:text-slate-900 mb-4"
      >
        <ArrowLeftIcon className="h-4 w-4 mr-1" />
        Back to Dashboard
      </button>

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Log a New Issue</h1>
        <p className="mt-2 text-slate-600 max-w-2xl">
          Fill in every required field so the team can reproduce and retest without going back to you.
          Fields marked <span className="text-red-500">*</span> are mandatory. Creates a helpdesk ticket
          and an Azure DevOps work item on <span className="font-medium">Exergy\Keepers</span>.
        </p>
        {currentUser && (
          <p className="text-sm text-slate-500 mt-2">
            Logging as: <span className="font-medium text-slate-700">{userName}</span>
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <button
          type="button"
          onClick={() => { setWorkItemType('Bug'); setError(null); setFieldErrors({}); }}
          className={`text-left rounded-xl border-2 p-4 transition-colors bg-white ${
            isBug ? 'border-red-500 shadow-sm' : 'border-slate-200 hover:border-slate-300'
          }`}
        >
          <div className="flex items-start gap-3">
            <BugAntIcon className={`h-7 w-7 ${isBug ? 'text-red-500' : 'text-slate-400'}`} />
            <div>
              <div className="font-semibold text-slate-900">Bug / Defect</div>
              <div className="text-sm text-slate-500 mt-0.5">Something is broken or behaving wrong.</div>
            </div>
          </div>
        </button>
        <button
          type="button"
          onClick={() => { setWorkItemType('UserStory'); setError(null); setFieldErrors({}); }}
          className={`text-left rounded-xl border-2 p-4 transition-colors bg-white ${
            !isBug ? 'border-sky-500 shadow-sm' : 'border-slate-200 hover:border-slate-300'
          }`}
        >
          <div className="flex items-start gap-3">
            <SparklesIcon className={`h-7 w-7 ${!isBug ? 'text-sky-500' : 'text-slate-400'}`} />
            <div>
              <div className="font-semibold text-slate-900">User Story</div>
              <div className="text-sm text-slate-500 mt-0.5">A new capability or change request.</div>
            </div>
          </div>
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
      )}
      {warning && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">{warning}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {isBug ? (
          <>
            <section className={sectionClass}>
              <h2 className="text-sm font-semibold tracking-wide text-sky-900 mb-4">
                1. SUMMARY &amp; CLASSIFICATION
              </h2>
              <div className="space-y-4">
                <div>
                  <FieldLabel hint="one line: what + where" required>Title</FieldLabel>
                  <input name="title" value={formData.title} onChange={handleChange} className={fieldClass('title')}
                    placeholder="Payment status not updating after approval on Claim Detail page" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabel hint="technical impact" required>Severity</FieldLabel>
                    <select name="severity" value={formData.severity} onChange={handleChange} className={fieldClass('severity')}>
                      {severities.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <FieldLabel hint="business urgency" required>Priority</FieldLabel>
                    <select name="priority" value={formData.priority} onChange={handleChange} className={fieldClass('priority')}>
                      {priorities.map((p) => <option key={String(p.value)} value={p.value}>{p.label}</option>)}
                    </select>
                    {fieldErrors.priority && <p className="mt-1 text-xs text-red-600">Required</p>}
                  </div>
                  <div>
                    <FieldLabel required>Reproducibility</FieldLabel>
                    <select name="reproducibility" value={formData.reproducibility} onChange={handleChange} className={fieldClass('reproducibility')}>
                      {reproducibilityOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <FieldLabel hint="what you were doing">Feature / Workflow</FieldLabel>
                    <input name="featureWorkflow" value={formData.featureWorkflow} onChange={handleChange} className={inputClass}
                      placeholder="Approving a medical payment" />
                  </div>
                </div>
              </div>
            </section>

            <section className={sectionClass}>
              <h2 className="text-sm font-semibold tracking-wide text-sky-900 mb-4">2. WHERE IT HAPPENED</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <FieldLabel required>Environment</FieldLabel>
                  <select name="environment" value={formData.environment} onChange={handleChange} className={fieldClass('environment')}>
                    {environments.map((env) => <option key={env} value={env}>{env}</option>)}
                  </select>
                </div>
                <div>
                  <FieldLabel hint="which build" required>Build / Version</FieldLabel>
                  <input name="buildVersion" value={formData.buildVersion} onChange={handleChange} className={fieldClass('buildVersion')}
                    placeholder="e.g. MPM v2.14.0 / build 3821" />
                </div>
                <div>
                  <FieldLabel required>Module / Page</FieldLabel>
                  <input name="modulePage" value={formData.modulePage} onChange={handleChange} className={fieldClass('modulePage')}
                    placeholder="Medical Payments > Claim Detail" />
                </div>
                <div>
                  <FieldLabel>URL / screen path</FieldLabel>
                  <input name="urlScreenPath" value={formData.urlScreenPath} onChange={handleChange} className={inputClass}
                    placeholder="https://uat.app/claims/12345/payments" />
                </div>
                <div>
                  <FieldLabel required>Browser / Device</FieldLabel>
                  <input name="browserDevice" value={formData.browserDevice} onChange={handleChange} className={fieldClass('browserDevice')}
                    placeholder="Chrome 126 / Windows 11" />
                </div>
                <div>
                  <FieldLabel required>Date observed</FieldLabel>
                  <input
                    type="date"
                    name="observedAt"
                    value={formData.observedAt}
                    onChange={handleChange}
                    className={fieldClass('observedAt')}
                  />
                </div>
              </div>
            </section>

            <section className={sectionClass}>
              <h2 className="text-sm font-semibold tracking-wide text-sky-900 mb-4">3. WHO &amp; WHAT DATA</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <FieldLabel>User / account</FieldLabel>
                  <input name="userAccount" value={formData.userAccount} onChange={handleChange} className={inputClass}
                    placeholder="test.user@example.com / underwriter role" />
                </div>
                <div>
                  <FieldLabel>Record / reference ID</FieldLabel>
                  <input name="recordReference" value={formData.recordReference} onChange={handleChange} className={inputClass}
                    placeholder="Claim #12345 / Policy POL-99881" />
                </div>
                <div className="md:col-span-2">
                  <FieldLabel>Sample data notes</FieldLabel>
                  <PasteScreenshotTextarea name="sampleDataNotes" rows={3} value={formData.sampleDataNotes} onChange={handleChange} onScreenshotPaste={handleScreenshotPaste} className={inputClass}
                    placeholder="Any sensitive-data notes, anonymised payloads, or related IDs. Paste a screenshot here to add it as an attachment." />
                </div>
              </div>
            </section>

            <section className={sectionClass}>
              <h2 className="text-sm font-semibold tracking-wide text-sky-900 mb-4">4. REPRODUCTION</h2>
              <div className="space-y-4">
                <div>
                  <FieldLabel required>Steps to reproduce</FieldLabel>
                  <PasteScreenshotTextarea name="stepsToReproduce" rows={5} value={formData.stepsToReproduce} onChange={handleChange} onScreenshotPaste={handleScreenshotPaste}
                    className={fieldClass('stepsToReproduce')}
                    placeholder={"1. Open claim Detail\n2. Approve payment\n3. Refresh status\n\nPaste a screenshot here to add it as an attachment."} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabel required>Expected result</FieldLabel>
                    <PasteScreenshotTextarea name="expectedResult" rows={4} value={formData.expectedResult} onChange={handleChange} onScreenshotPaste={handleScreenshotPaste}
                      className={fieldClass('expectedResult')} placeholder="Payment status shows Approved. Paste a screenshot here to add it as an attachment." />
                  </div>
                  <div>
                    <FieldLabel required>Actual result</FieldLabel>
                    <PasteScreenshotTextarea name="actualResult" rows={4} value={formData.actualResult} onChange={handleChange} onScreenshotPaste={handleScreenshotPaste}
                      className={fieldClass('actualResult')} placeholder="Status remains Pending. Paste a screenshot here to add it as an attachment." />
                  </div>
                </div>
                <div>
                  <FieldLabel hint="optional context">Additional notes</FieldLabel>
                  <PasteScreenshotTextarea name="additionalNotes" rows={3} value={formData.additionalNotes} onChange={handleChange} onScreenshotPaste={handleScreenshotPaste}
                    className={inputClass} placeholder="Workarounds, related tickets, etc. Paste a screenshot here to add it as an attachment." />
                </div>
              </div>
            </section>
          </>
        ) : (
          <>
            <section className={sectionClass}>
              <h2 className="text-sm font-semibold tracking-wide text-sky-900 mb-4">
                1. SUMMARY &amp; CLASSIFICATION
              </h2>
              <div className="space-y-4">
                <div>
                  <FieldLabel hint="short capability statement" required>Title</FieldLabel>
                  <input name="title" value={formData.title} onChange={handleChange} className={fieldClass('title')}
                    placeholder="Allow bulk approval of medical payments" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabel required>Priority</FieldLabel>
                    <select name="priority" value={formData.priority} onChange={handleChange} className={fieldClass('priority')}>
                      {priorities.map((p) => <option key={String(p.value)} value={p.value}>{p.label}</option>)}
                    </select>
                    {fieldErrors.priority && <p className="mt-1 text-xs text-red-600">Required</p>}
                  </div>
                  <div>
                    <FieldLabel required>Module / Page</FieldLabel>
                    <input name="modulePage" value={formData.modulePage} onChange={handleChange} className={fieldClass('modulePage')}
                      placeholder="Medical Payments > Payment Queue" />
                  </div>
                </div>
              </div>
            </section>

            <section className={sectionClass}>
              <h2 className="text-sm font-semibold tracking-wide text-sky-900 mb-4">2. THE STORY</h2>
              <div className="space-y-4">
                <div>
                  <FieldLabel required>As a… (role)</FieldLabel>
                  <input name="asARole" value={formData.asARole} onChange={handleChange} className={fieldClass('asARole')}
                    placeholder="Claims Assessor" />
                </div>
                <div>
                  <FieldLabel required>I want… (goal)</FieldLabel>
                  <input name="iWantGoal" value={formData.iWantGoal} onChange={handleChange} className={fieldClass('iWantGoal')}
                    placeholder="to approve multiple payments at once" />
                </div>
                <div>
                  <FieldLabel required>So that… (benefit / value)</FieldLabel>
                  <input name="soThatBenefit" value={formData.soThatBenefit} onChange={handleChange} className={fieldClass('soThatBenefit')}
                    placeholder="I can clear the daily queue without opening each claim individually" />
                </div>
                <div>
                  <FieldLabel>Description / context</FieldLabel>
                  <PasteScreenshotTextarea name="storyDescription" rows={4} value={formData.storyDescription} onChange={handleChange} onScreenshotPaste={handleScreenshotPaste}
                    className={inputClass}
                    placeholder="Today each payment must be opened and approved one by one… Paste a screenshot here to add it as an attachment." />
                </div>
              </div>
            </section>

            <section className={sectionClass}>
              <h2 className="text-sm font-semibold tracking-wide text-sky-900 mb-4">3. ACCEPTANCE CRITERIA</h2>
              <div>
                <FieldLabel hint="Given / When / Then" required>Acceptance criteria</FieldLabel>
                <PasteScreenshotTextarea name="acceptanceCriteria" rows={6} value={formData.acceptanceCriteria} onChange={handleChange} onScreenshotPaste={handleScreenshotPaste}
                  className={fieldClass('acceptanceCriteria')}
                  placeholder={"Given I am on the Payment Queue with items selected,\nWhen I click 'Approve selected',\nThen all selected payments move to 'Approved' and a confirmation shows the count.\n\nPaste a screenshot here to add it as an attachment."} />
              </div>
            </section>

            <section className={sectionClass}>
              <h2 className="text-sm font-semibold tracking-wide text-sky-900 mb-4">4. VALUE &amp; SUPPORTING DETAIL</h2>
              <div className="space-y-4">
                <div>
                  <FieldLabel required>Business value / impact</FieldLabel>
                  <PasteScreenshotTextarea name="businessValue" rows={3} value={formData.businessValue} onChange={handleChange} onScreenshotPaste={handleScreenshotPaste}
                    className={fieldClass('businessValue')}
                    placeholder="Saves ~30 min/assessor/day; reduces queue backlog at month-end. Paste a screenshot here to add it as an attachment." />
                </div>
                <div>
                  <FieldLabel>Dependencies / notes</FieldLabel>
                  <PasteScreenshotTextarea name="dependenciesNotes" rows={3} value={formData.dependenciesNotes} onChange={handleChange} onScreenshotPaste={handleScreenshotPaste}
                    className={inputClass}
                    placeholder="Depends on the approval permission role; needs audit-log entry per payment. Paste a screenshot here to add it as an attachment." />
                </div>
                <div>
                  <FieldLabel>Mockups / references</FieldLabel>
                  <input name="mockupLink" value={formData.mockupLink} onChange={handleChange} className={inputClass}
                    placeholder="Or paste a link to a mockup / spec…" />
                  <p className="mt-2 text-xs text-slate-500">You can also attach mockup files below.</p>
                </div>
              </div>
            </section>
          </>
        )}

        <section className="rounded-2xl border border-sky-100 bg-white p-5 sm:p-6">
          <h2 className="text-sm font-semibold tracking-wide text-slate-800 mb-3">
            {isBug ? '5. ATTACHMENTS' : '5. ATTACHMENTS / MOCKUPS'}
          </h2>
          <p className="text-sm text-slate-500 mb-3">
            Files are stored on the helpdesk ticket and also attached to the Azure DevOps work item.
            You can paste screenshots into any text field above or here (Ctrl+V).
          </p>
          <FileUpload
            onFileSelect={(file) => setSelectedFiles((prev) => [...prev, file])}
            onFileRemove={(index) => setSelectedFiles((prev) => prev.filter((_, i) => i !== index))}
            selectedFiles={selectedFiles}
            maxFiles={5}
            maxSizeMB={10}
          />
        </section>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2.5 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 bg-white hover:bg-slate-50"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || uploadingFiles}
            className="px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 disabled:opacity-50"
          >
            {loading
              ? 'Creating…'
              : uploadingFiles
                ? 'Uploading files…'
                : isBug
                  ? 'Create Bug'
                  : 'Create User Story'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ExergyIssueForm;
